import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { opcionesAPI, type MetodoPago } from "@/lib/api";
import { NumberField } from "@/components/ui/number-field";

interface FormData {
  codigo: string;
  tipo: string;
  interes: number;
  cuotasMax: number;
}

const emptyForm: FormData = {
  codigo: "",
  tipo: "",
  interes: 0,
  cuotasMax: 1
};

interface MetodosPagoABMProps {
  selectedItem?: MetodoPago | null;
  onBack?: () => void;
  onSaved?: () => void;
}

export function MetodosPagoABM({ selectedItem, onBack, onSaved: _onSaved }: MetodosPagoABMProps) {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [originalFormData, setOriginalFormData] = useState<FormData>(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredMetodos, setFilteredMetodos] = useState<MetodoPago[]>([]);
  const [allMetodos, setAllMetodos] = useState<MetodoPago[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await opcionesAPI.listMetodosPago();
        setAllMetodos(data);
      } catch (error) {
        console.error("Error loading metodos pago:", error);
      }
    };
    loadData();
  }, []);

  // Load selectedItem when passed from parent
  useEffect(() => {
    if (selectedItem) {
      selectMetodo(selectedItem);
    } else {
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
    }
  }, [selectedItem]);

  const handleTipoChange = (value: string) => {
    setFormData({ ...formData, tipo: value });
    checkForChanges({ ...formData, tipo: value });

    if (value.trim()) {
      const filtered = allMetodos.filter(m =>
        m.tipo?.toLowerCase().includes(value.toLowerCase()) ||
        m.codigo?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredMetodos(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectMetodo = (metodo: MetodoPago) => {
    const loadedData: FormData = {
      codigo: metodo.codigo,
      tipo: metodo.tipo,
      interes: metodo.interes,
      cuotasMax: metodo.cuotasMax || 1
    };
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setHasChanges(false);
    setShowSuggestions(false);
    setIsEditing(true);
  };

  const handleTipoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (filteredMetodos.length > 0) {
        selectMetodo(filteredMetodos[0]);
      } else if (formData.tipo.trim() && !isEditing) {
        setHasChanges(true);
        setShowSuggestions(false);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const checkForChanges = (data: FormData) => {
    const changed = JSON.stringify(data) !== JSON.stringify(originalFormData);
    setHasChanges(changed);
  };

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    checkForChanges(newData);
  };

  const handleSave = async () => {
    if (!formData.tipo.trim()) {
      toast.error("El tipo es requerido");
      return;
    }

    setLoading(true);
    try {
      if (isEditing && formData.codigo) {
        await opcionesAPI.updateMetodoPago(formData.codigo, {
          tipo: formData.tipo,
          interes: formData.interes,
          cuotasMax: formData.cuotasMax,
        });
        toast.success("Método de pago actualizado");
      } else {
        const newMetodo = await opcionesAPI.createMetodoPago({
          codigo: "",
          tipo: formData.tipo,
          interes: formData.interes,
          cuotasMax: formData.cuotasMax,
          activo: true,
        });
        setFormData({ ...formData, codigo: newMetodo.codigo });
        setIsEditing(true);
        toast.success("Método de pago creado");
      }

      const updated = await opcionesAPI.listMetodosPago();
      setAllMetodos(updated);
      setOriginalFormData(formData);
      setHasChanges(false);
    } catch (error: any) {
      console.error("Error saving metodo pago:", error);
      toast.error(error.message || "Error al guardar método de pago");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await opcionesAPI.deleteMetodoPago(formData.codigo);
      toast.success("Método de pago eliminado");

      const updated = await opcionesAPI.listMetodosPago();
      setAllMetodos(updated);
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
      setShowDeleteDialog(false);
    } catch (error: any) {
      console.error("Error deleting metodo pago:", error);
      toast.error(error.message || "Error al eliminar método de pago");
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setOriginalFormData(emptyForm);
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleNew}>
          Nuevo Método de Pago
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium mb-1">Tipo</label>
          <input
            type="text"
            value={formData.tipo}
            onChange={(e) => handleTipoChange(e.target.value)}
            onKeyDown={handleTipoKeyDown}
            onFocus={() => {
              if (formData.tipo.trim() && filteredMetodos.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Escriba para buscar o crear..."
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {showSuggestions && filteredMetodos.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredMetodos.map((metodo) => (
                <button
                  key={metodo.codigo}
                  type="button"
                  onClick={() => selectMetodo(metodo)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex justify-between items-center"
                >
                  <span>{metodo.tipo}</span>
                  <span className="text-xs text-muted-foreground">{metodo.codigo}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Interés (%)</label>
          <NumberField
            min="0"
            step="0.01"
            value={formData.interes}
            onChange={(e) => handleInputChange("interes", Number(e.target.value))}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Cuotas Máximas</label>
          <NumberField
            min="1"
            value={formData.cuotasMax}
            onChange={(e) => handleInputChange("cuotasMax", Number(e.target.value))}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
      </div>
      <FormFooter
        onBack={onBack}
        onDelete={handleDelete}
        deleteDisabled={!formData.codigo || loading}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saving={loading}
      />
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar este método de pago?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}